import React from "react";
import { Create } from "react-admin";
import { Box, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import ReasonSubreasonService from "../../services/ReasonSubreasonServices";
import { CreateReasonSubReason } from "../../interfaces/ReasonSubReason/CreateReasonSubReason";
import ReasonSubreasonFormFields, {
    ReasonSubreasonFormValue,
} from "./ReasonSubreasonFormFields";

const reasonSubreasonService = new ReasonSubreasonService();

const initialForm: ReasonSubreasonFormValue = {
    reason: "",
    subReason: "",
    clientId: "",
    module: "",
    actionType: "",
    isActive: true,
    isVisible: true,
    shouldCreateEscalation: false,
    markLeave: false,
    refundPercentage: "",
    cookPenaltyAmount: "",
    internalRatingParamId: "",
    trHi: "",
    trBn: "",
};

const numericOrNull = (value: any): number | null => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
};

const CreateReasonSubreasonForm = () => {
    const navigate = useNavigate();
    const [form, setForm] = React.useState<ReasonSubreasonFormValue>(initialForm);
    const [saving, setSaving] = React.useState<boolean>(false);

    const update = (key: keyof ReasonSubreasonFormValue, value: any) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const showError = (message: string) => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
            message,
            type: CustomSnackbarTypes.ERROR,
        });
    };

    const handleSubmit = async () => {
        if (!form.reason?.trim()) return showError("Reason is required");
        if (!form.module?.trim()) return showError("Module is required");
        const clientIdNum = numericOrNull(form.clientId);
        if (clientIdNum === null) return showError("Client Id is required");
        const refundPercentageNum = numericOrNull(form.refundPercentage);
        if (refundPercentageNum !== null && (refundPercentageNum < 80 || refundPercentageNum > 100)) {
            return showError("Refund % must be between 80 and 100");
        }

        const payload: CreateReasonSubReason = {
            reason: form.reason!.trim(),
            subReason: form.subReason?.trim() || undefined,
            clientId: clientIdNum,
            module: form.module!.trim(),
            actionType: form.actionType?.trim() || undefined,
            isActive: form.isActive,
            isVisible: form.isVisible,
            shouldCreateEscalation: form.shouldCreateEscalation,
            markLeave: form.markLeave,
            refundPercentage: refundPercentageNum,
            cookPenaltyAmount: numericOrNull(form.cookPenaltyAmount),
            internalRatingParamId: numericOrNull(form.internalRatingParamId),
            trHi: form.trHi?.trim() || undefined,
            trBn: form.trBn?.trim() || undefined,
        };

        setSaving(true);
        try {
            const res = await reasonSubreasonService.createReasonSubreason(payload);
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Reason / Sub-reason created",
                type: CustomSnackbarTypes.SUCCESS,
            });
            if (res?.data?.id) {
                navigate(`/ReasonSubreason/${res.data.id}/show`, { replace: true });
            } else {
                navigate(`/ReasonSubreason`, { replace: true });
            }
        } catch (err) {
            showError("Failed to create reason / sub-reason");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Create title="Create Reason / Sub-reason" resource="ReasonSubreason">
            <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <Stack sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        New Reason / Sub-reason
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Define a reason mapping with its module, behaviour and translations.
                    </Typography>
                </Stack>
                <ReasonSubreasonFormFields
                    mode="create"
                    value={form}
                    onChange={update}
                    onSubmit={handleSubmit}
                    onCancel={() => navigate("/ReasonSubreason")}
                    submitting={saving}
                />
            </Box>
        </Create>
    );
};

export default CreateReasonSubreasonForm;
